import { HTMLAttributes, useCallback } from 'react'
import { useLenis } from 'lenis/react'
import Link from 'next/link'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Grid from '@/components/base/grid'
import Media from '@/components/base/media'
// import Motion from '@/components/base/motion'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
// import { storeHeroRevealAnimation } from '@/components/sections/animations'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'
import { formatString } from '@/utils/strings'

import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

type StoreHeroProps = HTMLAttributes<HTMLElement> & {
  pageProps: WPPageData
}

const StoreHero = ({ pageProps, className, ...props }: StoreHeroProps) => {
  const { title, featured_image } = pageProps
  const { strings } = useStore()
  const typology = pageProps?.taxonomies?.['store-typology']?.[0].name
  const lenis = useLenis()

  const handleContactClick = useCallback(() => {
    const formEl = document.getElementById('store-form')
    if (formEl && lenis) {
      const headerHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--header-height'
        )
      )
      lenis.scrollTo(formEl, { offset: -headerHeight })
    } else {
      window.location.href = `mailto:${pageProps?.acf?.email?.to}`
    }
  }, [lenis, pageProps?.acf?.email?.to])

  const contactCtaLabel = strings?.labels.store_contact_hero_cta
    ? strings.labels.store_contact_hero_cta.includes('%s')
      ? formatString(strings.labels.store_contact_hero_cta, typology)
      : `${strings.labels.store_contact_hero_cta}`
    : null

  return (
    <>
      {/* <Motion animation={storeHeroRevealAnimation}>
        {(motionRef) => ( */}
      <div
        // ref={motionRef}
        className={cn([
          'relative -mt-[var(--header-height)] h-[calc(100*var(--lvh))] bg-[black]',
          {
            'h-[calc(100*var(--lvh))]': typology !== 'Store',
            'h-[24rem]': typology === 'Store'
          },
          className
        ])}
        {...props}
      >
        {featured_image?.url && (
          <div className="absolute inset-0 z-0 bg-[black]">
            <Media
              media={{
                poster: featured_image,
                poster_mobile: featured_image
              }}
              unlockLoaderOnReady
              priority
              data-motion-media
              className={cn('absolute left-0 top-0 h-full w-full opacity-60')}
            />
          </div>
        )}

        <Container className="dark relative z-10 h-full">
          <Grid className="relative h-full">
            {strings?.labels?.sales_network_link?.url && (
              <div className="absolute left-0 top-header-height w-full">
                <Spacer variant="xs" />
                <div className="mt-spacing-2xs flex gap-spacing-2xs">
                  <Link
                    data-motion-cta
                    href={`${strings.labels.sales_network_link.url}`}
                    scroll={false}
                    className={cn([
                      'flex size-[3.2rem] items-center justify-center rounded-radius-minimal border border-[rgba(255,255,255,0.3)]',
                      'transition-all duration-300 hover:bg-[rgba(255,255,255,0.3)]'
                    ])}
                  >
                    <SVGIcon
                      symbol="arrow-prev"
                      className="h-[1.6rem] w-[1.6rem] fill-white-light"
                      aria-hidden="true"
                    />
                  </Link>
                  {typology ? (
                    <div
                      className={cn([
                        'flex h-[3.2rem] items-center rounded-radius-minimal border border-[rgba(255,255,255,0.3)] px-spacing-2xs'
                      ])}
                    >
                      <Typography
                        variant="regular"
                        component="span"
                        className="condensed text-text-primary-dark"
                        data-motion-title
                      >
                        {typology}
                      </Typography>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {typology !== 'Store' ? (
              <div className="col-span-2 md:col-span-5 lg:col-span-5">
                <div
                  className={cn(
                    'flex h-full flex-col justify-end gap-spacing-xl pb-[140px] md:pb-spacing-2xl'
                  )}
                >
                  {title?.rendered ? (
                    <Typography variant="h2" component="h1" data-motion-title>
                      {title.rendered}
                    </Typography>
                  ) : null}
                  {
                    <div className="absolute bottom-[20px] left-0 right-auto md:bottom-spacing-2xl md:left-auto md:right-0">
                      <CtaButton
                        data-motion-cta
                        variant="primary"
                        onClick={handleContactClick}
                      >
                        {contactCtaLabel}
                      </CtaButton>
                    </div>
                  }
                </div>
              </div>
            ) : null}
          </Grid>
        </Container>
      </div>
      {/* )}
      </Motion> */}
      <Spacer variant="xxl" />
    </>
  )
}

export default StoreHero
